import * as React from 'react';
import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { IncidentType, PortalSharedExternalLinkView, PortalSharedResourceView, PortalSharedSurveyReportView } from '../../amorg-common';
import { applicationEndpoints } from '../../amorg-common/applicationEndpoints';
import { portalClientApi } from '../../api/portalClientApi';
import { mediaQuery } from '../../designSystem/BreakPoints';
import { Card, CardVariant } from '../../designSystem/Card';
import { CardButton } from '../../designSystem/CardButton';
import { Flex, FlexItem, Grid } from '../../designSystem/compositeComponents/FlexHelpers';
import { IncidentIcon } from '../../designSystem/compositeComponents/IncidentIcon';
import { iconName } from '../../designSystem/icons/Icons';
import { SingleColumnLayout } from '../_layouts/SingleColumnLayout';
import { PageHeader } from '../../designSystem/PageHeader';
import { PageSection } from '../../designSystem/PageSection';
import { P } from '../../designSystem/typography/Body';
import { H3, H4 } from '../../designSystem/typography/Heading';
import { EmployeePortalPageContext } from '../_contexts/EmployeePortalPageContext';
import { EmployeePageWrapper } from '../_pageWrappers/EmployeePageWrapper';
import { DashboardFactCard } from '../dashboard/_components/DashboardFactCard';
import { PortalListRowItem } from './_components/PortalListRowItem';
import { LatestPortalFiles } from './files/LatestPortalFiles';

// type loginMethod = 'none' |'magicLink';

export function PortalHomePage(): JSX.Element {

    useEffect(() => {
        document.title = 'Amorgy - medarbejderportal';
    }, []);

    return (
        <EmployeePageWrapper>
            <PortalHomePageContent />
        </EmployeePageWrapper>
    );
}

function PortalHomePageContent(): JSX.Element {
    const [redirectUrl, setRedirectUrl] = React.useState('');
    const [resources, setResources] = React.useState<PortalSharedResourceView[]>([]);

    const ctx = React.useContext(EmployeePortalPageContext);

    React.useEffect(() => {
        const getData = async () => {
            const resources = await portalClientApi.getPortalSharedResources();
            setResources(resources);
        };
        getData();
    }, []);

    const reports = React.useMemo(() => {
        const surveyReports:PortalSharedSurveyReportView[] = resources.filter((r): r is PortalSharedSurveyReportView => r.resourceType === 'SurveyReport');
        surveyReports.sort((a,b) => new Date(b.surveyEnd).getTime() - new Date(a.surveyEnd).getTime());
        if(surveyReports.length > 3) {
            surveyReports.splice(3);
        }

        return surveyReports;
    }, [resources]);

    const externalLinks = React.useMemo(() => {
        const links:PortalSharedExternalLinkView[] = resources.filter((r): r is PortalSharedExternalLinkView => r.resourceType === 'ExternalLink');
        if(links.length > 3) {
            links.splice(3);
        }
        return links;
    }, [resources]);

    return (
        <>
            {redirectUrl !== '' &&
                <Navigate to={redirectUrl} />
            }

            <PageHeader narrow headerText={`Velkommen til arbejdsmiljøportalen`} headerStyle='primary-black' />
            <SingleColumnLayout narrow>
                <PortalHomeGrid className="portalHomeGrid">
                    <TopArea>
                        <PageSection
                            header={{
                                title: 'Indberetninger',
                            }}
                            headerStyleVariant='minimal'
                            contentStyleVariant='minimal'
                            contentPadding="16px 0"
                        >
                            
                            <Grid gap={16} gridTemplateColumns={2}>
                                <IncidentCardButton type='whistleblower' variant='darkBlue'
                                    header='Whistleblower'
                                    // description='Her kan du indsende en whistleblower-rapport, hvis du har oplysninger om alvorlige forhold, som bør undersøges nærmere.'
                                    description='Anonym indberetning'
                                    onClick={() => setRedirectUrl(applicationEndpoints.portal_incidents_type.replace(':type', 'whistleblower'))}
                                />

                                <IncidentCardButton type="almost-accident"
                                    header="Nærved-ulykke"
                                    // description="Her registrerer du nærved-ulykker – situationer hvor noget kunne være gået galt, eller næsten gjorde det. Formålet er at forebygge ulykker ved at lære af hændelserne."
                                    description="Undgå fremtidige fejl"
                                    onClick={() => setRedirectUrl(applicationEndpoints.portal_incidents_type.replace(':type', 'almost-accident'))}
                                />

                                <IncidentCardButton type="issue"
                                    header="Arbejdsmiljø"
                                    // description="Her kan du indrapportere hændelser og udfordringer, der vedrører arbejdsmiljøet. Det kan være alt fra manglende udstyr og fysiske fejl i omgivelserne til behov for ændrede arbejdsgange."
                                    description="Hændelse eller problem"
                                    onClick={() => setRedirectUrl(applicationEndpoints.portal_incidents_type.replace(':type', 'issue'))}
                                />

                                <IncidentCardButton type="work-injury"
                                    header="Arbejdsskade"
                                    // description="Hvis du har været udsat for en arbejdsulykke, der har resulteret i en skade, skal den indberettes. Det kan være alt fra mindre skader til alvorlige ulykker."
                                    description="Hurtig registrering"
                                    onClick={() => setRedirectUrl(applicationEndpoints.portal_incidents_type.replace(':type', 'work-injury'))}
                                />
                                </Grid> 
                            
                        </PageSection>

                        <PageSection
                            header={{
                                title: 'Vidste du at',
                            }}
                            headerStyleVariant='minimal'
                            contentStyleVariant='minimal'
                            contentPadding="16px 0"
                        >
                            <Grid gridTemplateColumns={1} gap={16}>
                            
                                <DashboardFactCard portal />
                                {/* <Card variant='outlined' flexGrow='1' style={{ width: '100%' }} shadow>
                                    <Flex flexDirection='column' gap={16} width='100%'>
                                        <H4>Humør smitter</H4>
                                        <P fontSize='small'>Positive kollegaer og god stemning øger den generelle trivsel og kan reducere konflikter og misforståelser.</P>
                                    </Flex>
                                </Card> */}
                            
                                <CardButton variant='outlined' onClick={() => setRedirectUrl(applicationEndpoints.portal_organization)} flexGrow='1' style={{ width: '100%' }} shadow>
                                    <Flex gap={16} flexDirection='column' grow='1'>
                                        <H3>Din organisation</H3>
                                        <P fontSize='small' style={{ padding: 0, margin: 0 }}>Se hvordan din Arbejdsmiljøorganisation er struktureret og hvem der repræsentere dig og dine kollegaer.</P>
                                    </Flex>
                                </CardButton>
                            
                            </Grid>
                        </PageSection>
                    </TopArea>
                    <MiddleArea>
                        <PageSection
                            header={{
                                title: 'APV rapporter',
                                button: {
                                    text: 'Se alle',
                                    variant: 'outlined',
                                    onClick: () => setRedirectUrl(applicationEndpoints.portal_survey_reports)
                                }
                            }}
                            headerStyleVariant='minimal'
                            contentStyleVariant='minimal'
                            contentPadding="16px 0"
                        >
                            
                            <Card variant='outlined' flexGrow='1' style={{ width: '100%' }} shadow>
                                <Flex flexDirection='column' gap={8} width='100%'>
                                    {reports.length === 0 && <P>Ingen rapporter er delt</P>}

                                    {reports.map(r => (
                                        <a href={applicationEndpoints.portal_survey_report_portalResourceId.replace(':portalResourceId', r.id)} 
                                        style={{ 
                                            textDecoration: 'none', 
                                            width: '100%' 
                                        }} 
                                        key={r.id}>
                                            <PortalListRowItem
                                                key={r.id}
                                                title={r.activityTitle + ' - ' + r.segmentViewName}
                                                date={`Afsluttet d. ${new Date(r.surveyEnd).toLocaleDateString()}`}
                                                icon='arrows_chevron_chevronForward' />
                                        </a>
                                    ))}
                                    
                                </Flex>
                            </Card>
                        </PageSection>
                    </MiddleArea>            
                        {/* <Flex flexDirection='column' gap={8} alignItems='flex-start' width='100%' grow='1'>
                            <Flex flexDirection='row' justifyContent='space-between' alignItems='center' width='100%'>
                                <H2>Handleplaner</H2>
                                <DarkLink href={applicationEndpoints.portal_actionplans} >Se alle</DarkLink>
                            </Flex>
                            <Card variant='outlined' flexGrow='1' style={{ width: '100%' }} shadow>
                                <Flex flexDirection='column' gap={8} width='100%'>
                                    <LatestPortalActionPlans />
                                    
                                </Flex> 
                            </Card>
                        </Flex> */}

                    <BottomArea>
                        
                        <PageSection
                            header={{
                                title: 'Dokumenter og filer',
                                button: {
                                    text: 'Se alle',
                                    variant: 'outlined',
                                    onClick: () => setRedirectUrl(applicationEndpoints.portal_files)
                                }
                            }}
                            headerStyleVariant='minimal'
                            contentStyleVariant='minimal'
                            contentPadding="16px 0"
                        >
                                    
                            <Card variant='outlined' flexGrow='1' style={{ width: '100%' }} shadow>
                                <Flex flexDirection='column' gap={8} width='100%'>

                                    <LatestPortalFiles />

                                </Flex>
                            </Card>
                        </PageSection>
                        
                        <PageSection
                            header={{
                                title: 'Links',
                                button: {
                                    text: 'Se alle',
                                    variant: 'outlined',
                                    onClick: () => setRedirectUrl(applicationEndpoints.portal_links)
                                }
                            }}
                            headerStyleVariant='minimal'
                            contentStyleVariant='minimal'
                            contentPadding="16px 0"
                        >
                        
                            <Card variant='outlined' flexGrow='1' style={{ width: '100%' }} shadow>
                                <Flex flexDirection='column' gap={16} width='100%'>
                                    {externalLinks.length === 0 && <P>Ingen links er delt</P>}

                                    {externalLinks.map(link => 
                                        <a href={link.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', width: '100%', color: ctx.colors.text.normal }} key={link.id}>
                                            <PortalListRowItem
                                                title={link.title}
                                                description={link.description || undefined}
                                                icon={link.iconName as iconName || 'interface_actions_externalLink'}
                                            />
                                        </a>
                                    )}
                                </Flex>
                            </Card>
                        </PageSection>
                    </BottomArea>
                </PortalHomeGrid>
            </SingleColumnLayout>
        </>

    );
}

type IncidentCardButtonProps = {
    type: IncidentType,
    variant?: CardVariant,
    header: string,
    description: string,
    onClick: () => void
}
function IncidentCardButton(props: IncidentCardButtonProps): JSX.Element {
    return (
        <CardButton variant={props.variant ?? 'outlined'} onClick={props.onClick} shadow>
            <Flex flexDirection='column' gap={4}>
                {props.variant === 'darkBlue' ? <IncidentIcon type={props.type} /> : <IncidentIcon type={props.type} />}

                <FlexItem flexGrow='1'>
                    {props.variant === 'darkBlue' ? <H4 color={"white"}>{props.header}</H4> : <H4>{props.header}</H4>}
                    <P fontSize='small'>{props.description}</P>
                </FlexItem>
            </Flex>
        </CardButton>
    );
}

const PortalHomeGrid = styled.div`
    display: grid;
    gap: 32px;
    grid-template-areas:
        "topArea"
        "middleArea"
        "bottomArea"    
    ;
    grid-template-columns: 1fr;
`;

const TopArea = styled.div`
    grid-area: topArea;
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
    
    ${mediaQuery.tablet} {
        grid-template-columns: 2fr 1fr;
    }
`;

const MiddleArea = styled.div`
    grid-area: middleArea;
`


const BottomArea = styled.div`
    grid-area: bottomArea;
    grid-template-columns: 1fr;
    display: grid;
    gap: 16px;

    ${mediaQuery.tablet} {
        grid-template-columns: repeat(2, 1fr);
    }

    ${mediaQuery.desktop} {
        grid-template-columns: repeat(2, 1fr);
    }

`;